import React, { useEffect, useState } from 'react'
import { BLOCKS } from '@contentful/rich-text-types'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { ChevronDownIcon, FAQBackArrowIcon } from 'components_v2/Icons'
import {
  ArticleList,
  BreadcrumbBar,
  PageLayout,
  PageHeader
} from '../__shared__'
import { Box, Grid, Image } from 'ui/acro'
import styled, { css } from 'styled-components'
import Text from 'ui/acro/typography/Text'
import { useWindowResize } from 'hooks'

const MobileAccordion = styled(Box)`
  ${css({
    'details[open]': {
      svg: {
        transform: 'rotate(-180deg)' //rotate chevron when details box is open
      }
    },
    summary: {
      listStyle: 'none',
      '::-webkit-details-marker, ::marker': {
        display: 'none'
      },
      svg: {
        transition: 'all .1s linear'
      }
    },
    '.support-link': {
      ':hover': {
        '@media only screen and (min-width: 1024px)': {
          backgroundColor: '#F2F2F2',
          marginLeft: '-3px',
          borderLeft: '3px solid black'
        }
      },
      '@media only screen and (max-width: 767px)': {
        '.support-link-inner': {
          lineHeight: '24.96px',
          padding: '10px',
          margin: '10px 0',
          span: {
            textDecoration: 'none'
          }
        }
      }
    }
  })}
`

const ArticleBody = styled(Box)(() =>
  css({
    p: {
      fontFamily: 'proxima-nova',
      fontSize: '16px',
      fontStyle: 'normal',
      fontWeight: '400',
      display: 'block',
      lineHeight: '23px',
      letterSpacing: '0.02em',
      minHeight: '25px',
      padding: '10px 0',
      img: {
        '@media screen and (min-width: 1024px)': {
          minWidth: '500px',
          textAlign: 'center'
        }
      }
    },
    'h1, h2, h3, h4, h5, h6, p, ol, ul': {
      lineHeight: '25px',
      fontFamily: 'proxima-nova'
    },
    ol: {
      listStyle: 'decimal',
      marginLeft: '20px'
    },
    ul: {
      listStyle: 'disc',
      marginLeft: '20px'
    },
    a: {
      textDecoration: 'underline'
    }
  })
)

const ArticleView = ({ slug, category, articleList, article }) => {
  const [size, setSize] = useState()
  const { width } = useWindowResize()
  useEffect(() => {
    setSize(width < 768 ? 'mobile' : 'desktop')
  }, [width])
  const isMobile = size === 'mobile'

  if (!slug || !articleList || !article) {
    return false
  }

  const articleTitle = article.items[0].fields.title
  const body = article.items[0].fields.content
  const categoryForTitle = category.replace(/-/g, ' ')

  return (
    <>
      <Box
        py={[null, '30px', '30px']}
        display={['none', 'block', 'block']}
        backgroundColor='#FAFAFA'
      >
        <PageHeader title='FAQ Articles' />
      </Box>
      <PageLayout>
        <BreadcrumbBar categoryName={category} slug={slug} />
        <Grid
          gridTemplateColumns={['100%', '23% 72%', '20% 70%', '22% 73%']}
          gridColumnGap={[null, '30px', '60px']}
          marginTop={[null, '20px', '20px', '35px']}
        >
          <MobileAccordion>
            <Box
              as={isMobile ? 'details' : 'div'}
              borderBottom={['1px solid #E1E1E1', 'none']}
            >
              <Text
                as={isMobile ? 'summary' : 'div'}
                fontFamily={['proxima-nova', 'arquitecta']}
                fontWeight={['600', '900']}
                fontStyle='normal'
                fontSize={['16px', '20px']}
                textTransform={['none', 'uppercase']}
                lineHeight={['140%', '23px']}
                borderTop={['1px solid #E1E1E1', 'none']}
                padding={['10px 3px', '0']}
                marginTop={['15px', '0']}
                maxWidth={['none', '300px']}
                textAlign='left'
              >
                <Text display={['inline', 'none']}>More </Text>
                {`articles about ${categoryForTitle}`}
                <>
                  <Box
                    display={['inline', 'none']}
                    style={{ float: 'right' }}
                    margin='0 0px 0 7px'
                  >
                    <ChevronDownIcon color='black' />
                  </Box>
                </>
              </Text>
              <Box>
                <ArticleList
                  articleList={articleList}
                  category={category}
                  hideLine={true}
                />
              </Box>
            </Box>
          </MobileAccordion>
          <Box pb='25px'>
            <Box
              as='a'
              className='unstyled'
              href={`/support/${category}/`} //styleName: cta;
              display={['none', 'flex']}
            >
              <FAQBackArrowIcon />
              <Text
                pl='15px'
                fontFamily='Arquitecta'
                fontSize='15px'
                textTransform='uppercase'
                fontweight='400'
                lineHeight='20px'
                letterSpacing='0.08em'
              >
                {' '}
                Back
              </Text>
            </Box>
            <Box pt={['30px', '50px']} pb='30px'>
              <Text
                as='h2'
                fontFamily='proxima-nova'
                fontWeight='700'
                fontSize='20px'
                lineHeight='23px'
              >
                {articleTitle}
              </Text>
            </Box>
            {article && (
              <ArticleBody as='article' pb={['30px', '45px']}>
                {documentToReactComponents(body, {
                  renderNode: {
                    [BLOCKS.EMBEDDED_ASSET]: ({ data }) => {
                      const assetObj = article.includes.asset.find(
                        ({ sys }) => sys.id === data.target.sys.id
                      )

                      // we can pass params to the url object to set the height and width. This is useful to reduce the overall payload of the site.
                      // For more info: https://github.com/contentful/contentful.rb#assets
                      return (
                        <p>
                          <Image
                            src={`${assetObj.fields.file.url}?h=500&q=100`}
                            alt={assetObj.fields.title}
                          />
                        </p>
                      )
                    }
                  }
                })}
              </ArticleBody>
            )}
            <Text
              as='a'
              href='/contact-us'
              textTransform='uppercase'
              fontFamily='Arquitecta'
              fontSize='15px'
              fontStyle='normal'
              fontWeight='400'
              lineHeight='20px'
              letterSpacing='0.08em'
            >
              Need More Help?{' '}
              <span style={{ textDecoration: 'underline' }}>Contact Us</span>
            </Text>
          </Box>
        </Grid>
      </PageLayout>
    </>
  )
}

export default ArticleView
